import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  alpha,
  Breadcrumbs,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

interface Account {
  id: string;
  name: string;
  accountNumber: string;
  type: string;
}

interface AccountSettings {
  customerDefaultAccount: string;
  supplierDefaultAccount: string;
}

const LinkedAccountSettings: React.FC = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState<AccountSettings>({
    customerDefaultAccount: '',
    supplierDefaultAccount: ''
  });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // جلب الحسابات والإعدادات عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // جلب الحسابات النشطة
        const accountsData = await apiService.get<Account[]>('/finance/accounts/selection/active');
        setAccounts(accountsData);

        // جلب إعدادات الحسابات المرتبطة
        try {
          const settingsData = await apiService.get<AccountSettings>('/settings/linked-accounts');
          if (settingsData) {
            setSettings(settingsData);
          }
        } catch (settingsError) {
          console.log('لم يتم العثور على إعدادات الحسابات المرتبطة، سيتم استخدام القيم الافتراضية');
          // استمر باستخدام القيم الافتراضية
        }
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
        setError('حدث خطأ أثناء جلب البيانات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // حفظ الإعدادات
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // إرسال الإعدادات إلى API
      await apiService.post('/settings/linked-accounts', settings);
      
      setSuccess(true);
      // إخفاء رسالة النجاح بعد 3 ثوان
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('خطأ في حفظ الإعدادات:', error);
      setError('حدث خطأ أثناء حفظ الإعدادات. يرجى المحاولة مرة أخرى.');
    } finally {
      setSaving(false);
    }
  };

  // تحديث الإعدادات عند تغيير الحقول
  const handleChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setSettings(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <SettingsIcon sx={{ mr: 1, color: '#30c3b0' }} />
              إعدادات الحسابات المرتبطة
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/settings" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                الإعدادات
              </Link>
              <Typography color="text.primary">إعدادات الحسابات المرتبطة</Typography>
            </Breadcrumbs>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* عرض التحميل أثناء جلب البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                الحسابات الرئيسية الافتراضية
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                يتم استخدام هذه الحسابات عند إنشاء حسابات تلقائية للعملاء والموردين. سيتم إنشاء حساب فرعي تحت الحساب الرئيسي المحدد.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>الحساب الرئيسي للعملاء</InputLabel>
                <Select
                  name="customerDefaultAccount"
                  value={settings.customerDefaultAccount}
                  onChange={handleChange}
                  label="الحساب الرئيسي للعملاء"
                >
                  <MenuItem value="">
                    <em>اختر حسابًا</em>
                  </MenuItem>
                  {accounts
                    .filter(account => account.type === 'رئيسي')
                    .map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.accountNumber} - {account.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>الحساب الرئيسي للموردين</InputLabel>
                <Select
                  name="supplierDefaultAccount"
                  value={settings.supplierDefaultAccount}
                  onChange={handleChange}
                  label="الحساب الرئيسي للموردين"
                >
                  <MenuItem value="">
                    <em>اختر حسابًا</em>
                  </MenuItem>
                  {accounts
                    .filter(account => account.type === 'رئيسي')
                    .map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.accountNumber} - {account.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              {/* رسائل الخطأ والنجاح */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  تم حفظ الإعدادات بنجاح
                </Alert>
              )}
              
              {/* زر الحفظ */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    bgcolor: '#30c3b0',
                    '&:hover': { bgcolor: '#25a899' },
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                  }}
                >
                  {saving ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'حفظ الإعدادات'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default LinkedAccountSettings;